import React from 'react';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';

import { EditablePlusOne, PlusOneGuest, SingleEventPlusOneGuestInput } from 'types/plus-one.types';
import {
  initializeQuestionnaire,
  QuestionnaireFormValues,
  useQuestionnaireSchema
} from '@epoch-components/QuestionnaireFields/schema';
import { TypedQuestion } from 'types/question-response.types';

interface PlusOneSchemaParams {
  collectQuestionnaireResponses: boolean;
  questions: TypedQuestion[];
}

export type BasePlusOneFormValue = Omit<EditablePlusOne, 'questionnaireResponses' | 'email'>;
export type PlusOneWithQuestionnaireFormValue = BasePlusOneFormValue & {
  questionnaireResponses: QuestionnaireFormValues;
};
export type PlusOneFormValue = BasePlusOneFormValue | PlusOneWithQuestionnaireFormValue;

export const plusOneHasQuestionnaireValues = (
  value: PlusOneFormValue
): value is PlusOneWithQuestionnaireFormValue => {
  return 'questionnaireResponses' in value;
};

export interface UsePlusOneSchemaReturn {
  plusOneSchema: z.ZodType<PlusOneFormValue>;
  formatPlusOne: (plusOne: PlusOneGuest) => PlusOneFormValue;
  newPlusOne: () => PlusOneFormValue;
  parsePlusOneFormValue: (formValue: PlusOneFormValue) => SingleEventPlusOneGuestInput;
}

export const usePlusOneSchema = ({
  collectQuestionnaireResponses,
  questions
}: PlusOneSchemaParams): UsePlusOneSchemaReturn => {
  const { questionnaireSchema, parseQuestionnaireFormValues } = useQuestionnaireSchema({
    hasQuestionnaire: collectQuestionnaireResponses,
    questions
  });
  const plusOneSchema = React.useMemo(() => {
    return z.object({
      uuid: z.string(),
      firstName: z.string().min(1, 'First name is required'),
      lastName: z.string().min(1, 'Last name is required'),
      plusOneId: z.number().optional(),
      ...(collectQuestionnaireResponses
        ? { questionnaireResponses: questionnaireSchema }
        : undefined)
    });
  }, [questionnaireSchema]);

  const formatPlusOne = React.useCallback(
    (plusOne: PlusOneGuest): PlusOneFormValue => ({
      plusOneId: plusOne.plusOneId,
      uuid: uuidv4(),
      firstName: plusOne.firstName,
      lastName: plusOne.lastName,
      ...(collectQuestionnaireResponses
        ? {
            questionnaireResponses: initializeQuestionnaire(
              questions,
              collectQuestionnaireResponses ? plusOne.questionnaireResponses : []
            )
          }
        : undefined)
    }),
    [initializeQuestionnaire, collectQuestionnaireResponses, questions]
  );

  const newPlusOne = React.useCallback(
    (): PlusOneFormValue => ({
      uuid: uuidv4(),
      firstName: '',
      lastName: '',
      ...(collectQuestionnaireResponses
        ? { questionnaireResponses: initializeQuestionnaire(questions) }
        : undefined)
    }),
    [initializeQuestionnaire, collectQuestionnaireResponses, questions]
  );

  const parsePlusOneFormValue = React.useCallback(
    (formValue: PlusOneFormValue): SingleEventPlusOneGuestInput => {
      const { plusOneId, firstName, lastName } = formValue;
      return {
        firstName,
        lastName,
        ...(plusOneId ? { plusOneId } : undefined),
        ...(plusOneHasQuestionnaireValues(formValue)
          ? {
              questionnaireResponses: parseQuestionnaireFormValues(formValue.questionnaireResponses)
            }
          : {})
      };
    },
    [parseQuestionnaireFormValues]
  );

  return {
    plusOneSchema,
    formatPlusOne,
    newPlusOne,
    parsePlusOneFormValue
  };
};
